import React, { useCallback, useRef, useState } from 'react';
import { isLayerVisibleAtClipTime } from '@/lib/layers/isLayerVisibleAtClipTime';
import { isClipMirror, isVisualLayer } from '@/lib/layers/types';
import type { MediaLayer, LayerRect, LayerFrame, VisualLayer } from '@/lib/layers/types';
import { applyReframePan } from '@/lib/layers/applyReframePan';
import { applyCanvasMove, applyCanvasResize } from '@/lib/layers/applyCanvasDrag';
import { computeSnap, type SnapGuides, type SnapTarget } from '@/lib/layers/computeSnap';
import { computeResizeSnap } from '@/lib/layers/computeResizeSnap';
import { createRafScheduler } from '@/lib/dom/createRafScheduler';

const NEUTRAL_FRAME: LayerFrame = { scale: 1, offsetX: 0, offsetY: 0 };

/** How close, in on-screen pixels, a moving layer must come to a guide before
 *  it latches on. Converted to a per-axis fraction of the composition, since a
 *  portrait composition's width and height are different pixel spans. */
const SNAP_PX = 8;

const NO_GUIDES: SnapGuides = { x: null, y: null };

const GUIDE_OFFSET: Record<SnapTarget, string> = { start: '0%', center: '50%', end: '100%' };

interface Props {
  layers: MediaLayer[];
  activeLayerId: string | null;
  onSelect: (id: string) => void;
  onChange: (id: string, rect: LayerRect, rotation: number) => void;
  /** id of the layer currently being reframed (pan-inside-rect mode) — null = normal move/resize. */
  reframeLayerId: string | null;
  onFrameChange: (id: string, frame: LayerFrame) => void;
  /** Playhead in CLIP time. Layers outside their window are not on screen, so
   *  they get no transform handles — you can't select or drag what you can't
   *  see. */
  currentClipTime: number;
}

type DragMode =
  | { kind: 'move' }
  | { kind: 'pan' }
  | { kind: 'resize'; corner: 'nw' | 'ne' | 'sw' | 'se' }
  | { kind: 'rotate' };

export const LayerCanvasEditor: React.FC<Props> = ({
  layers, activeLayerId, onSelect, onChange, reframeLayerId, onFrameChange,
  currentClipTime,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  /** Guides currently latched, drawn only for the duration of a drag. */
  const [guides, setGuides] = useState<SnapGuides>(NO_GUIDES);
  const drag = useRef<{
    mode: DragMode; startX: number; startY: number; rect: LayerRect; rotation: number; id: string;
    /** Frame at drag start — panning accumulates from this, like rect does for move/resize. */
    frame: LayerFrame;
    /** Uploaded media keeps its aspect; a clip mirror is a freely sized crop
     *  box whose footage already renders with object-fit: cover. */
    lockAspect: boolean;
  } | null>(null);

  // Only layers actually on screen right now are interactive. Drags are begun
  // from this list, so they can never target a hidden layer.
  const visualLayers = layers
    .filter(isVisualLayer)
    .filter((l) => isLayerVisibleAtClipTime(l, currentClipTime));

  const toFraction = useCallback((clientX: number, clientY: number) => {
    const box = containerRef.current!.getBoundingClientRect();
    return { fx: (clientX - box.left) / box.width, fy: (clientY - box.top) / box.height };
  }, []);

  const onChangeRef = useRef(onChange);
  const onFrameChangeRef = useRef(onFrameChange);
  onChangeRef.current = onChange;
  onFrameChangeRef.current = onFrameChange;
  const changeSchedulerRef = useRef(createRafScheduler(
    (next: { id: string; rect: LayerRect; rotation: number }) => {
      onChangeRef.current(next.id, next.rect, next.rotation);
    },
  ));
  const frameSchedulerRef = useRef(createRafScheduler(
    (next: { id: string; frame: LayerFrame }) => {
      onFrameChangeRef.current(next.id, next.frame);
    },
  ));

  const onPointerMove = useCallback((e: PointerEvent) => {
    const d = drag.current;
    if (!d || !containerRef.current) return;
    const box = containerRef.current.getBoundingClientRect();
    const dx = (e.clientX - d.startX) / box.width;
    const dy = (e.clientY - d.startY) / box.height;

    if (d.mode.kind === 'pan') {
      // Fractions of the BOX (the layer's own pixel size), not the container.
      const dxFrac = d.rect.w > 0 ? dx / d.rect.w : 0;
      const dyFrac = d.rect.h > 0 ? dy / d.rect.h : 0;
      frameSchedulerRef.current.schedule({
        id: d.id,
        frame: applyReframePan({ frame: d.frame, dxFrac, dyFrac }),
      });
      return;
    }

    let rect = d.rect;
    let rotation = d.rotation;

    if (d.mode.kind === 'move') {
      // No clamp — layers move freely, including fully offscreen.
      rect = applyCanvasMove({ rect: d.rect, dx, dy });
      // Snapping is a translation, so it can never distort the layer. Skipped
      // for a rotated layer, whose visible bounds are not its layout box, and
      // bypassed while Ctrl/Cmd is held so a layer can be nudged past a guide.
      // (Not Alt: window managers commonly swallow Alt-drag.)
      const snapping = !e.ctrlKey && !e.metaKey && d.rotation === 0;
      const snapped = computeSnap({
        rect,
        thresholdX: snapping ? SNAP_PX / box.width : 0,
        thresholdY: snapping ? SNAP_PX / box.height : 0,
      });
      rect = snapped.rect;
      setGuides(snapped.guides);
    } else if (d.mode.kind === 'resize') {
      rect = applyCanvasResize({ rect: d.rect, corner: d.mode.corner, dx, dy, lockAspect: d.lockAspect });
      // Resize snaps only the edges the dragged corner owns. Aspect-locked
      // layers snap the nearest axis and the other dimension follows; the
      // anchored corner never moves. Same Ctrl/Cmd bypass and rotation guard
      // as move-snapping.
      const snapping = !e.ctrlKey && !e.metaKey && d.rotation === 0;
      const snapped = computeResizeSnap({
        rect,
        corner: d.mode.corner,
        lockAspect: d.lockAspect,
        thresholdX: snapping ? SNAP_PX / box.width : 0,
        thresholdY: snapping ? SNAP_PX / box.height : 0,
      });
      rect = snapped.rect;
      setGuides(snapped.guides);
    } else if (d.mode.kind === 'rotate') {
      const cx = d.rect.x + d.rect.w / 2;
      const cy = d.rect.y + d.rect.h / 2;
      const { fx, fy } = toFraction(e.clientX, e.clientY);
      rotation = Math.round((Math.atan2(fy - cy, fx - cx) * 180) / Math.PI + 90);
    }
    changeSchedulerRef.current.schedule({ id: d.id, rect, rotation });
  }, [toFraction]);

  const endDrag = useCallback(() => {
    changeSchedulerRef.current.flush();
    frameSchedulerRef.current.flush();
    drag.current = null;
    setGuides(NO_GUIDES);
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', endDrag);
  }, [onPointerMove]);

  /** Takes the layer explicitly rather than reading `active`: on the first
   *  pointerdown of an unselected layer, `onSelect` has not flushed yet, so
   *  `active` is still the PREVIOUS selection (or null). Reading it there
   *  skipped both the drag and the stopPropagation, so the first click on a
   *  layer never dragged and the event escaped to the elements underneath. */
  const beginDrag = useCallback((mode: DragMode, e: React.PointerEvent, layer: VisualLayer) => {
    // Only the primary button drags. Right-click must fall through to the
    // canvas context menu instead of starting a move/resize/rotate.
    if (e.button !== 0) return;
    e.stopPropagation();
    drag.current = {
      mode, startX: e.clientX, startY: e.clientY,
      rect: layer.rect, rotation: layer.rotation, id: layer.id,
      frame: (layer.type === 'video' && layer.frame) || NEUTRAL_FRAME,
      // Motion layers scale their whole design canvas into the rect, so an
      // unlocked resize would stretch every glyph and shape. Clip mirrors
      // instead cover their rect, allowing independent split-screen boxes.
      lockAspect: layer.type === 'image' || (layer.type === 'video' && !isClipMirror(layer)) || layer.type === 'motion',
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endDrag);
  }, [onPointerMove, endDrag]);

  return (
    // pointer-events-none on the container so empty space (including the
    // common zero-layers case) doesn't block clicks/drags on the video
    // player underneath (Remotion's clickToPlay + built-in controls).
    // Each layer box below explicitly re-enables pointer-events.
    <div ref={containerRef} className="absolute inset-0 z-20 pointer-events-none">
      {/* Snap guides. Nudged back half a pixel so the hairline straddles the
          composition edge instead of sitting just inside (or just outside) it. */}
      {guides.x && (
        <div
          data-testid={`snap-guide-x-${guides.x}`}
          className="absolute top-0 bottom-0 w-px bg-primary"
          style={{ left: GUIDE_OFFSET[guides.x], marginLeft: -0.5 }}
        />
      )}
      {guides.y && (
        <div
          data-testid={`snap-guide-y-${guides.y}`}
          className="absolute left-0 right-0 h-px bg-primary"
          style={{ top: GUIDE_OFFSET[guides.y], marginTop: -0.5 }}
        />
      )}
      {visualLayers.map((l) => {
        const isReframing = l.id === reframeLayerId && l.type === 'video';
        return (
        <div
          key={l.id}
          data-layer-ui
          onPointerDown={(e) => {
            onSelect(l.id);
            beginDrag(isReframing ? { kind: 'pan' } : { kind: 'move' }, e, l);
          }}
          className={`absolute pointer-events-auto ${isReframing ? 'cursor-grab' : 'cursor-move'} ${l.id === activeLayerId ? 'ring-2 ring-primary' : ''}`}
          style={{
            left: `${l.rect.x * 100}%`, top: `${l.rect.y * 100}%`,
            width: `${l.rect.w * 100}%`, height: `${l.rect.h * 100}%`,
            transform: `rotate(${l.rotation}deg)`, transformOrigin: 'center center',
          }}
        >
          {l.id === activeLayerId && !isReframing && (
            <>
              {(['nw', 'ne', 'sw', 'se'] as const).map((corner) => (
                <div
                  key={corner}
                  data-testid={`layer-resize-${corner}`}
                  onPointerDown={(e) => beginDrag({ kind: 'resize', corner }, e, l)}
                  className="absolute h-3 w-3 rounded-full bg-primary"
                  style={{
                    left: corner.includes('w') ? -6 : undefined,
                    right: corner.includes('e') ? -6 : undefined,
                    top: corner.includes('n') ? -6 : undefined,
                    bottom: corner.includes('s') ? -6 : undefined,
                    cursor: `${corner}-resize`,
                  }}
                />
              ))}
              <div
                onPointerDown={(e) => beginDrag({ kind: 'rotate' }, e, l)}
                className="absolute left-1/2 h-3 w-3 -translate-x-1/2 rounded-full bg-primary"
                style={{ top: -24, cursor: 'grab' }}
              />
            </>
          )}
        </div>
        );
      })}
    </div>
  );
};

export default LayerCanvasEditor;
